import { zodResolver } from '@hookform/resolvers/zod'
import { authService, queryClientOptions } from '@libs/data-access/queries'
import type { CountryCode } from '@libs/ui/ds'
import {
  AlertErrorAdapter,
  Button,
  Flex,
  ProgressStepper,
  Text,
  Title,
  notifications,
  CountryAutocomplete,
  RadioButton,
  phoneInputUtils
} from '@libs/ui/ds'
import { BaseForm, Controller, ControllerBase } from '@libs/ui/form'
import { ENABLE_NEW_COMPANY_SIZE } from '@libs/utils/environments'
import { useOnboardingTranslation } from '@libs/utils/translations'
import { GTM } from '@spa/core/externals/gtm'
import { submitRecaptchaValidation } from '@spa/core/externals/recaptcha'
import { getCurrentPhoneIndicator } from '@spa/scenes/Auth/legacy/SignUpLegacy/AuthPhoneInput/getCurrentPhoneIndicator'
import { useAtomTrackingDatas } from '@spa/scenes/Auth/shared/atoms/atomTrackingDatas'
import { useSignIn } from '@spa/scenes/Auth/shared/services'
import type { FC } from 'react'
import { useForm, Controller as RHFController } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'

import {
  isProfileValid,
  useAccountCreation
} from '../../../hooks/useAccountCreation'

import {
  isAccountCompanyInfoValid,
  useAccountCompanyInfoSchema
} from './AccountCompanyInfoForm.validations'
import type { AccountCompanyInfoFormType } from './AccountCompanyInfoForm.validations'

export const AccountCompanyInfoForm: FC = () => {
  const { t } = useOnboardingTranslation()
  const signInMutation = useSignIn()
  const getDraftAccountQuery = authService.useGetDraftAccount({
    staleTime: queryClientOptions.time.STATIC
  })
  const createSignupMutation = authService.usePostSignupCreate()
  const { profile, handleCompanyComplete, updateDraftData, email } =
    useAccountCreation()

  const companyQualificationSchema = useAccountCompanyInfoSchema()

  const [atomTrackingDatas, setAtomTrackingDatas] = useAtomTrackingDatas()

  const locationOptions = [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2-5', label: '2-5' },
    { value: '6-20', label: '6-20' },
    { value: '+20', label: '+20' }
  ]

  const legacyEmployeeCountOptions = [
    { value: '0-5', label: t('onboarding.employeeCount.0-5') },
    { value: '6-10', label: t('onboarding.employeeCount.6-10') },
    { value: '11-15', label: t('onboarding.employeeCount.11-15') },
    { value: '16-30', label: t('onboarding.employeeCount.16-30') },
    { value: '31-80', label: t('onboarding.employeeCount.31-80') },
    { value: '81+', label: t('onboarding.employeeCount.81+') }
  ]

  const employeeCountOptions = [
    { value: '0-5', label: t('onboarding.employeeCount.0-5') },
    { value: '6-14', label: t('onboarding.employeeCount.6-14') },
    { value: '15-99', label: t('onboarding.employeeCount.15-99') },
    { value: '100+', label: t('onboarding.employeeCount.100+') }
  ]

  const industryOptions = [
    {
      value: 'Restauration traditionnelle',
      label: t('onboarding.industry.conventionalCatering')
    },
    { value: 'Restauration rapide', label: t('onboarding.industry.fastFood') },
    { value: 'Hôtellerie', label: t('onboarding.industry.hotelBusiness') },
    { value: 'Boulangerie-pâtisserie', label: t('onboarding.industry.bakery') },
    { value: 'Alimentaire', label: t('onboarding.industry.dietary') },
    { value: 'Santé & Pharmacie', label: t('onboarding.industry.health') },
    { value: 'Retail & boutiques', label: t('onboarding.industry.retail') },
    { value: 'Loisirs & Sports', label: t('onboarding.industry.leisure') },
    { value: 'Services', label: t('onboarding.industry.services') },
    { value: 'Autres', label: t('onboarding.industry.others') }
  ]

  const isCompanyProfileValid = isAccountCompanyInfoValid(profile)

  const methods = useForm<AccountCompanyInfoFormType>({
    mode: 'onBlur',
    resolver: zodResolver(companyQualificationSchema),
    defaultValues: {
      locationCount: isCompanyProfileValid ? profile.locationCount : '',
      employeeCount: isCompanyProfileValid ? profile.employeeCount : '',
      industry: isCompanyProfileValid ? profile.industry : '',
      isFranchise: isCompanyProfileValid ? profile.isFranchise : false,
      country: isCompanyProfileValid ? profile.country : ''
    }
  })

  const {
    control,
    formState: { errors, isSubmitting },
    setError
  } = methods

  const onSubmitHandler: SubmitHandler<
    AccountCompanyInfoFormType
  > = async formData => {
    try {
      // Update draft data first
      await updateDraftData({
        locationCount: formData.locationCount,
        employeeCount: formData.employeeCount,
        industry: formData.industry,
        isFranchise: formData.isFranchise,
        country: formData.country
      })

      // Get the latest draft data
      const { data: draftAccount } = await getDraftAccountQuery.refetch()
      const profile = draftAccount?.draft_account_form_data?.profile

      if (!profile || !isProfileValid(profile)) {
        setError('root', {
          type: 'manual',
          message: t('onboarding.qualification.error.missingProfile')
        })
        return
      }

      // Validate reCAPTCHA
      try {
        await submitRecaptchaValidation()
      } catch (error) {
        setError('root', {
          type: 'manual',
          message: t('onboarding.qualification.error.recaptcha')
        })
        return
      }

      // Create signup
      await createSignupMutation.mutateAsync({
        payload: {
          account: {
            name: profile.companyName,
            country: profile.country
          },
          membership: {
            firstname: profile.firstName,
            lastname: profile.lastName,
            phone: phoneInputUtils.transformToInternationalFormat({
              number: profile.phone.number,
              countryCode: profile.phone.countryCode as CountryCode
            }) as string
          },
          tracking: {
            job: profile.jobTitle,
            location_count: profile.locationCount,
            employee_count: profile.employeeCount,
            industry: profile.industry,
            is_franchise: profile.isFranchise,
            ...atomTrackingDatas
          }
        }
      })

      GTM.trackLeadFreeTrial(
        {
          firstname: profile.firstName,
          lastname: profile.lastName,
          phone_number: profile.phone.number,
          email: email ?? '',
          phone_indicator: getCurrentPhoneIndicator(
            profile.phone.countryCode.toLocaleLowerCase()
          )
        },
        {
          tracking_datas: {
            job: profile.jobTitle,
            location_count: profile.locationCount,
            employee_count: profile.employeeCount,
            industry: profile.industry,
            is_franchise: profile.isFranchise
          },
          country: profile.country
        }
      )

      setAtomTrackingDatas({})

      // Sign in with refresh token
      await signInMutation.withRefreshToken()

      // Show success notification
      notifications.success({
        message: t('onboarding.qualification.success')
      })

      // Complete the company info step
      handleCompanyComplete()
    } catch (error) {
      setError('root', {
        type: 'manual',
        message: t('onboarding.qualification.error.submit')
      })
      notifications.error({
        message: t('onboarding.qualification.error.submit')
      })
    }
  }

  const formError = errors.root
    ? {
        message: errors.root.message || ''
      }
    : null

  return (
    <Flex className='w-full'>
      <ProgressStepper
        currentStep={2}
        totalSteps={2}
        color='primary'
        className='mb-10'
      />
      <Flex className='mb-10 gap-2'>
        <Title size={2}>{t('onboarding.qualification.company.title')}</Title>
        <Text className='text-s'>
          {t('onboarding.qualification.company.subtitle')}
        </Text>
      </Flex>

      <BaseForm methods={methods} onSubmit={onSubmitHandler} role='form'>
        <Flex className='flex-col gap-4'>
          <AlertErrorAdapter error={formError} data-testid='alert-form-error' />

          <RHFController
            control={control}
            name='locationCount'
            render={({ field }) => (
              <RadioButton.Group
                {...field}
                label={t('onboarding.qualification.company.locationCount')}
                errorMessage={errors?.locationCount?.message}
                required
                classNames={{
                  inner: 'flex-wrap'
                }}
              >
                {locationOptions.map(option => (
                  <RadioButton
                    data-testid={`signup-location-count-radiogroup-${option.value}`}
                    position='right'
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </RadioButton.Group>
            )}
          />

          <Controller.Select
            control={control}
            name='employeeCount'
            label={t('onboarding.qualification.company.employeeCount')}
            errorMessage={errors?.employeeCount?.message}
            options={
              ENABLE_NEW_COMPANY_SIZE === 'true'
                ? employeeCountOptions
                : legacyEmployeeCountOptions
            }
            placeholder={t(
              'onboarding.qualification.company.selectEmployeeCount'
            )}
            required
            data-testid='signup-employee-count-radiogroup'
          />

          <Controller.Select
            control={control}
            name='industry'
            label={t('onboarding.qualification.company.industry')}
            errorMessage={errors?.industry?.message}
            options={industryOptions}
            placeholder={t('onboarding.qualification.company.selectIndustry')}
            required
            data-testid='signup-company-industry-input'
          />

          <Controller.Checkbox
            control={control}
            name='isFranchise'
            label={t('onboarding.qualification.company.franchise')}
            position='right'
          />

          <ControllerBase
            control={control}
            name='country'
            field={
              <CountryAutocomplete
                label={t('onboarding.qualification.company.country')}
                errorMessage={errors?.country?.message}
                placeholder={t(
                  'onboarding.qualification.company.selectCountry'
                )}
                required
                data-testid='signup-user-country-input'
              />
            }
            onChangeAdapter={e => e}
          />

          <Flex className='mt-4'>
            <Button
              type='submit'
              data-testid='signup-submit-button'
              loading={isSubmitting}
              fillWidth
            >
              {t('onboarding.qualification.company.getStarted')}
            </Button>
          </Flex>
        </Flex>
      </BaseForm>
    </Flex>
  )
}
